import { Popover, Tooltip, ActionIcon, Text } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

/**
 * Small, consistent "info" affordance: a dimmed (i) icon that reveals a localized
 * explanation. Pass an i18n key (resolved from the Veft namespace) or a literal label.
 *
 *   <InfoHint i18nKey="enrichment_what_help" multiline />   // click → Popover (long text)
 *   <InfoHint i18nKey="linkedin_url_field_help" />          // hover → Tooltip (one-liner)
 *
 * Multi-line help opens on click so the text is readable/selectable on touch devices too;
 * one-liners use a hover Tooltip. Help strings live in i18n.js under the _help/_hint/_tooltip
 * convention (see the comment at the top of the Veft namespace).
 */
export default function InfoHint({
  i18nKey,
  label,
  multiline = false,
  width = 300,
  size = 15,
  color = "gray",
  position = "top",
}) {
  const { t } = useTranslation("Veft");
  const [opened, { toggle, open, close }] = useDisclosure(false);

  const text = label ?? (i18nKey ? t(i18nKey) : "");
  if (!text) return null;

  if (multiline) {
    // Controlled Popover: close-on-outside-click and Escape route through onChange (not onClose)
    // in Mantine, so sync both here; the icon click toggles open/closed.
    return (
      <Popover opened={opened} onChange={(o) => (o ? open() : close())} width={width} position={position} withArrow shadow="md" withinPortal>
        <Popover.Target>
          <ActionIcon
            variant="subtle"
            color={color}
            size="xs"
            aria-label={text}
            onClick={(e) => { e.stopPropagation(); toggle(); }}
          >
            <IconInfoCircle size={size} />
          </ActionIcon>
        </Popover.Target>
        <Popover.Dropdown>
          <Text size="xs" style={{ whiteSpace: "pre-wrap" }}>{text}</Text>
        </Popover.Dropdown>
      </Popover>
    );
  }

  return (
    <Tooltip label={text} multiline w={width} withArrow position={position}>
      <ActionIcon variant="subtle" color={color} size="xs" aria-label={text}>
        <IconInfoCircle size={size} />
      </ActionIcon>
    </Tooltip>
  );
}
